<template>
  <div class="settings-page">
    <h2>Calendars</h2>
    <div class="subtitle">Manage your calendars displayed in the agenda.</div>

    <BaseIntegrationMissing
      v-if="hasCalendar !== 'synchronized'"
      feature="calendar"
      :synchronizing="hasCalendar === 'initializing'"
    />

    <template v-else>
      <section class="color-source">
        <h3>Color</h3>
        <p class="description">
          Decide how events and allocated tasks are colored, either based on the
          calendar they live in, the labels applied to them or a colored
          property you added.
        </p>
        <div class="entry">
          <label>Events</label>
          <div class="color-control">
            <div
              v-if="eventPreviewSwatches.length > 0"
              class="color-preview"
              :class="{ overflow: eventPreviewSwatches.length > 5 }"
            >
              <RTag
                v-for="(swatch, i) in eventPreviewSwatches"
                :key="`${swatch.color}-${swatch.label}-${i}`"
                :text="swatch.label"
                :theme="`calendar-${swatch.color}`"
                :tooltip="swatch.label"
                skin="dot"
                :has-padding="false"
                compact
              />
            </div>
            <PropertyPicker
              class="color-select"
              :types="types"
              :database="eventsDatabase"
              :validate="isColorSourceField"
              :static-options="eventColorStaticOptions"
              :model-value="eventColorSourcePath"
              :locked="isColoringLocked"
              @update:model-value="setEventColorSource"
              @locked-click="openColoringUpgrade"
            />
          </div>
        </div>
        <div class="entry">
          <label>Tasks</label>
          <div class="color-control">
            <div
              v-if="taskPreviewSwatches.length > 0"
              class="color-preview"
              :class="{ overflow: taskPreviewSwatches.length > 5 }"
            >
              <RTag
                v-for="(swatch, i) in taskPreviewSwatches"
                :key="`${swatch.color}-${swatch.label}-${i}`"
                :text="swatch.label"
                :theme="`calendar-${swatch.color}`"
                :tooltip="swatch.label"
                skin="dot"
                :has-padding="false"
                compact
              />
            </div>
            <PropertyPicker
              class="color-select"
              :types="types"
              :database="tasksDatabase"
              :validate="isColorSourceField"
              :static-options="taskColorStaticOptions"
              :model-value="taskColorSourcePath"
              :locked="isColoringLocked"
              @update:model-value="setTaskColorSource"
              @locked-click="openColoringUpgrade"
            />
          </div>
        </div>
      </section>

      <section class="default-calendar">
        <h3>Default Calendar</h3>
        <p class="description">
          Events and time-blocked tasks will be created in these calendars by
          default.
        </p>
        <div class="entry">
          <label>Events</label>
          <VSelect
            class="calendar-select"
            :clearable="false"
            :searchable="false"
            :reduce="(calendar: CalendarOption) => calendar.id"
            :options="calendarsOptions"
            :model-value="defaultCalendar?.id"
            @update:model-value="setDefaultCalendar"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
            <template #selected-option="{ id, label }">
              <CalendarTag
                v-if="id"
                class="select-calendar-color"
                :value="id"
                display="icon-only"
              />
              <span>{{ label }}</span>
            </template>

            <template #option="{ id, label }">
              <CalendarTag
                class="select-calendar-color"
                :value="id"
                display="icon-only"
              />
              <span>{{ label }}</span>
            </template>
          </VSelect>
        </div>
        <BaseAlert v-if="defaultCalendarWarning" state="warning" size="small">
          {{ defaultCalendarWarning }}
        </BaseAlert>
        <div class="entry">
          <label>Tasks</label>
          <VSelect
            class="calendar-select"
            :clearable="false"
            :searchable="false"
            :reduce="(calendar: CalendarOption) => calendar.id"
            :options="calendarsOptions"
            :model-value="taskDefaultCalendarSelection"
            @update:model-value="setTaskDefaultCalendar"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
            <template #selected-option="{ id, label }">
              <CalendarTag
                v-if="id"
                class="select-calendar-color"
                :value="id"
                display="icon-only"
              />
              <span>{{ label }}</span>
            </template>

            <template #option="{ id, label }">
              <CalendarTag
                class="select-calendar-color"
                :value="id"
                display="icon-only"
              />
              <span>{{ label }}</span>
            </template>
          </VSelect>
        </div>
      </section>

      <section class="calendars-section">
        <div class="calendar-zone">
          <h3>Active Calendars</h3>
          <BaseAlert state="info" size="small">
            Active calendars are calendars holding events that you will be
            attending: personal, professional, family etc. Active events will
            appear in the Agenda and set as busy.
          </BaseAlert>
          <Drop
            :accepts-type="['calendar']"
            :class="[
              'calendar-list',
              { 'drag-active': isDragging, 'drop-over': isDraggingOverActive },
            ]"
            @dragenter="isDraggingOverActive = true"
            @dragleave="isDraggingOverActive = false"
            @drop="onDropActive"
          >
            <Drag
              v-for="calendar in activeCalendars"
              :key="calendar.id"
              :data="calendar"
              :type="'calendar'"
              :disabled="isDefaultCalendar(calendar.id)"
              @dragend="isDragging = false"
            >
              <CalendarInline
                :class="{ 'is-default': isDefaultCalendar(calendar.id) }"
                :id="calendar.id"
                :disabled="calendar.disabled"
                :display-identity="displayIdentity"
                :summary="calendar.summary"
                :role="calendar.role"
                :color="calendar.color"
                :owner="calendar.connection"
                @handle-down="isDragging = true"
                @select-color="(id, color) => selectColor(id, color)"
                @toggle="(id, enabled) => toggle(id, enabled)"
              />
            </Drag>
          </Drop>
        </div>

        <div class="calendar-zone">
          <h3>Passive Calendars</h3>
          <BaseAlert state="info" size="small">
            Passive calendars are calendars holding events that are important
            for planning but that you will not be attending: holidays,
            birthdays, colleagues' etc. Passive events are visible in the
            Calendar but hidden in the Agenda and set as free.
          </BaseAlert>
          <Drop
            :accepts-type="['calendar']"
            :class="[
              'calendar-list',
              {
                'drag-active': isDragging,
                'drop-over': isDraggingOverPassive,
              },
            ]"
            @dragenter="isDraggingOverPassive = true"
            @dragleave="isDraggingOverPassive = false"
            @drop="onDropPassive"
          >
            <Drag
              v-for="calendar in passiveCalendars"
              :key="calendar.id"
              :data="calendar"
              :type="'calendar'"
              :disabled="isDefaultCalendar(calendar.id)"
              @dragend="isDragging = false"
            >
              <CalendarInline
                :class="{ 'is-default': isDefaultCalendar(calendar.id) }"
                :id="calendar.id"
                :disabled="calendar.disabled"
                :display-identity="displayIdentity"
                :summary="calendar.summary"
                :role="calendar.role"
                :color="calendar.color"
                :owner="calendar.connection"
                @handle-down="isDragging = true"
                @select-color="(id, color) => selectColor(id, color)"
                @toggle="(id, enabled) => toggle(id, enabled)"
              />
            </Drag>
          </Drop>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { isEqual } from "lodash-es";
import { computed, defineComponent, ref, watch } from "vue";
import { Drag, Drop } from "vue-easy-dnd";

import { Calendar, CalendarColor } from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseIntegrationMissing from "@/components/base/BaseIntegrationMissing.vue";
import CalendarTag from "@/components/elements/CalendarTag.vue";
import RTag from "@/components/elements/RTag.vue";
import {
  CALENDAR_COLOR_SOURCE_ID,
  colorSourceFromPropertyPath,
  colorSourcePreviewSwatches,
  colorSourcePropertyPath,
  isColorSourceField,
  labelsColorProperty,
  loadedColorSource,
  resolvedColorSource,
} from "@/components/graph/calendarColorSource";
import { databaseForTable } from "@/components/graph/predicates";
import PropertyPicker from "@/components/graph/PropertyPicker.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import * as inject from "@/components/inject";
import { getLabels, labelsFromGraphResult } from "@/components/labels";
import { openUpgradeModal } from "@/components/modals/upgrade";
import CalendarInline from "@/components/settings/CalendarInline.vue";
import { EVENTS_TABLE_ID, TASKS_TABLE_ID } from "@/components/tables";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useCalendarColorSources } from "@/composables/useCalendarColorSources";
import {
  asTaskDefaultCalendarId,
  defaultCalendarIssue,
  useTaskDefaultCalendar,
  type DefaultCalendarIssue,
} from "@/composables/useCalendars";
import { useReleaser } from "@/ref";

/** Entitlement gating the event/task color-source pickers. */
const EVENT_COLORING_ENTITLEMENT = "event-coloring";

type CalendarOption = {
  id: string;
  label: string;
};

export default defineComponent({
  components: {
    Drag,
    Drop,
    CalendarInline,
    ChevronDownIcon,
    CalendarTag,
    PropertyPicker,
    RTag,
    BaseAlert,
    BaseIntegrationMissing,
  },
  async setup() {
    const controller = inject.controller();
    const hasCalendar = inject.hasCalendar();
    const types = inject.types();
    const profile = inject.profile();
    const billing = inject.billing();
    const releaser = useReleaser();
    const colorSources = releaser.add(useCalendarColorSources(profile));
    const taskDefaultCalendar = releaser.add(useTaskDefaultCalendar(profile));
    const browserStore = useBrowserStore(profile.value.user.accountId);
    const workspaceId = computed(() => profile.value.user.personal.workspaceId);
    const eventColorSource = colorSources.events;
    const taskColorSource = colorSources.tasks;
    const eventsDatabase = computed(() =>
      databaseForTable(types.value, EVENTS_TABLE_ID)
    );
    const tasksDatabase = computed(() =>
      databaseForTable(types.value, TASKS_TABLE_ID)
    );
    const resolvedEventColorSource = computed(() =>
      resolvedColorSource(
        eventColorSource.value,
        types.value,
        eventsDatabase.value
      )
    );
    const resolvedTaskColorSource = computed(() =>
      resolvedColorSource(
        taskColorSource.value,
        types.value,
        tasksDatabase.value
      )
    );
    const eventColorSourcePath = computed(() =>
      colorSourcePropertyPath(resolvedEventColorSource.value)
    );
    const taskColorSourcePath = computed(() =>
      colorSourcePropertyPath(resolvedTaskColorSource.value)
    );
    // Calendar first, then the special Labels property (`labels` source),
    // which the picker pins here instead of listing it as a normal property.
    const colorStaticOptions = (
      database: typeof eventsDatabase
    ): { id: string; label: string }[] => {
      const options = [{ id: CALENDAR_COLOR_SOURCE_ID, label: "Calendar" }];
      const labelsProperty = labelsColorProperty(database.value);
      if (labelsProperty) {
        options.push({ id: labelsProperty.id, label: labelsProperty.name });
      }
      return options;
    };
    const eventColorStaticOptions = computed(() =>
      colorStaticOptions(eventsDatabase)
    );
    const taskColorStaticOptions = computed(() =>
      colorStaticOptions(tasksDatabase)
    );
    const persistResolvedColorSource = (
      stored: typeof eventColorSource,
      database: typeof eventsDatabase
    ) => {
      releaser.defer(
        watch(
          [stored, types],
          () => {
            const resolved = loadedColorSource(
              stored.value,
              types.value,
              database.value
            );
            if (!isEqual(stored.value, resolved)) stored.value = resolved;
          },
          { immediate: true }
        )
      );
    };
    persistResolvedColorSource(eventColorSource, eventsDatabase);
    persistResolvedColorSource(taskColorSource, tasksDatabase);
    // Entitlement gate on the color-source pickers: fail open while the
    // entitlement is still loading (undefined), lock on an explicit false.
    const isColoringLocked = computed(
      () =>
        billing.value?.isEntitledTo(EVENT_COLORING_ENTITLEMENT)?.value === false
    );
    const openColoringUpgrade = () =>
      openUpgradeModal(billing, undefined, EVENT_COLORING_ENTITLEMENT);
    const setEventColorSource = (path: string[]) => {
      eventColorSource.value = colorSourceFromPropertyPath(
        path,
        eventsDatabase.value
      );
    };
    const setTaskColorSource = (path: string[]) => {
      taskColorSource.value = colorSourceFromPropertyPath(
        path,
        tasksDatabase.value
      );
    };
    const isDragging = ref(false);
    const clearDragging = () => {
      isDragging.value = false;
    };
    document.addEventListener("pointerup", clearDragging);
    releaser.defer(() =>
      document.removeEventListener("pointerup", clearDragging)
    );

    const setDefaultCalendar = async (calendarId: string) => {
      // The picker only offers eligible calendars, but re-check anyway: a
      // disabled or read-only default silently breaks event creation.
      const calendar = await controller.calendar.get(calendarId);
      if (calendar.value?.isWritable && !calendar.value.disabled) {
        controller.calendar.selectDefault(calendarId);
      }
      calendar.release();
    };
    const [calendars, profiles, defaultCalendar, labelsResult] = await (() => {
      const calendars = releaser.add(controller.calendar.all());
      const profiles = releaser.add(controller.auth.profiles());
      const defaultCalendar = releaser.add(controller.calendar.defaultCal());
      const labelsResult = releaser.add(
        getLabels(workspaceId.value, controller)
      );
      return Promise.all([calendars, profiles, defaultCalendar, labelsResult]);
    })();

    const calendarsOptions = computed<CalendarOption[] | undefined>(() =>
      calendars.value
        ?.filter((calendar) => calendar.isWritable && !calendar.disabled)
        .map((calendar) => {
          return {
            id: calendar.id,
            label: calendar.summary,
          };
        })
    );
    // The task default calendar (a local preference, unlike the
    // controller-managed events default): the stored value resolved against
    // the current calendars, like the color sources — a calendar that no
    // longer exists, is turned off, or is read-only counts as unset (kept
    // as-is while calendars are still loading, so a valid choice is not
    // overwritten).
    const resolvedTaskDefaultCalendarId = computed(() => {
      const id = asTaskDefaultCalendarId(taskDefaultCalendar.value);
      if (!id || calendars.value === undefined) return id;
      const calendar = calendars.value.find((cal) => cal.id === id);
      return calendar && calendar.isWritable && !calendar.disabled ? id : null;
    });
    // Persist the resolution, like `persistResolvedColorSource` above —
    // seeding an unset (or no longer valid) value from the events default
    // calendar when that one is itself eligible.
    releaser.defer(
      watch(
        [taskDefaultCalendar, calendars, defaultCalendar],
        () => {
          if (calendars.value === undefined) return;
          const resolved =
            resolvedTaskDefaultCalendarId.value ??
            (defaultCalendarIssue(defaultCalendar.value) === null
              ? (defaultCalendar.value?.id ?? null)
              : null);
          if (taskDefaultCalendar.value !== resolved)
            taskDefaultCalendar.value = resolved;
        },
        { immediate: true }
      )
    );
    const taskDefaultCalendarSelection = computed(
      () => resolvedTaskDefaultCalendarId.value ?? undefined
    );
    const setTaskDefaultCalendar = (calendarId: string) => {
      taskDefaultCalendar.value = calendarId;
    };
    const previewLabels = computed(() =>
      labelsFromGraphResult(labelsResult.value)
    );
    const eventPreviewSwatches = computed(() =>
      colorSourcePreviewSwatches({
        source: resolvedEventColorSource.value,
        types: types.value,
        database: eventsDatabase.value,
        store: browserStore,
        calendars: calendars.value ?? [],
        labels: previewLabels.value,
      })
    );
    const taskPreviewSwatches = computed(() =>
      colorSourcePreviewSwatches({
        source: resolvedTaskColorSource.value,
        types: types.value,
        database: tasksDatabase.value,
        store: browserStore,
        calendars: calendars.value ?? [],
        labels: previewLabels.value,
      })
    );
    const activeCalendars = computed<Calendar[]>(() => {
      const list =
        calendars.value?.filter(
          (cal) => cal.role === "primary" || cal.role === "default"
        ) ?? [];
      const defaultId = defaultCalendar.value?.id;
      return defaultId
        ? [...list].sort((a, b) =>
            a.id === defaultId ? -1 : b.id === defaultId ? 1 : 0
          )
        : list;
    });
    const passiveCalendars = computed<Calendar[]>(() => {
      const list =
        calendars.value?.filter((cal) => cal.role === "secondary") ?? [];
      const defaultId = defaultCalendar.value?.id;
      return defaultId
        ? [...list].sort((a, b) =>
            a.id === defaultId ? -1 : b.id === defaultId ? 1 : 0
          )
        : list;
    });

    const isDraggingOverActive = ref(false);
    const isDraggingOverPassive = ref(false);

    const displayIdentity = computed<boolean>(() => profiles.value.length > 1);

    const selectColor = (id: string, color: CalendarColor) => {
      controller.calendar.select(id, { color });
    };

    const toggle = (id: string, enabled: boolean) => {
      // Never disable the default calendar: events could no longer be
      // created in it. The row hides the toggle for the default, but guard
      // here too in case the two ever disagree.
      if (!enabled && isDefaultCalendar(id)) return;
      controller.calendar.select(id, { disabled: !enabled });
    };

    const isDefaultCalendar = (calendarId: string): boolean => {
      return defaultCalendar.value?.id === calendarId;
    };

    // Settings-specific copy: "turned off" matches the on/off toggle shown in
    // the calendar lists below (where a turned-off default can be turned back
    // on), instead of the internal "disabled" term. The "missing" case is
    // safe here: this screen only renders once the integration synchronized.
    const warningFor = (issue: DefaultCalendarIssue | null): string | null => {
      switch (issue) {
        case "missing":
          return (
            "You have no default calendar, so events can't be created. " +
            "Pick one above."
          );
        case "disabled":
          return (
            "This calendar is turned off in the calendar lists below, so " +
            "events can't be added to it. Turn it back on, or pick another " +
            "default calendar."
          );
        case "not-writable":
          return (
            "You don't have permission to add events to this calendar. " +
            "Pick another default calendar."
          );
        case null:
          return null;
      }
    };
    const defaultCalendarWarning = computed(() =>
      warningFor(defaultCalendarIssue(defaultCalendar.value))
    );

    const onDropActive = (data: { data?: Calendar }) => {
      isDragging.value = false;
      isDraggingOverActive.value = false;
      if (!data || !data.data) return;
      const calendar = data.data;
      if (calendar.role === "secondary") {
        controller.calendar.select(calendar.id, { role: "primary" });
      }
    };

    const onDropPassive = (data: { data?: Calendar }) => {
      isDragging.value = false;
      isDraggingOverPassive.value = false;
      if (!data || !data.data) return;
      const calendar = data.data;
      if (calendar.role === "primary" || calendar.role === "default") {
        controller.calendar.select(calendar.id, { role: "secondary" });
      }
    };
    return {
      hasCalendar,
      activeCalendars,
      passiveCalendars,
      isDragging,
      isDraggingOverActive,
      isDraggingOverPassive,
      calendarsOptions,
      defaultCalendar,
      defaultCalendarWarning,
      displayIdentity,
      eventColorSourcePath,
      eventColorStaticOptions,
      eventPreviewSwatches,
      eventsDatabase,
      isColoringLocked,
      isColorSourceField,
      isDefaultCalendar,
      onDropActive,
      onDropPassive,
      openColoringUpgrade,
      selectColor,
      setDefaultCalendar,
      setEventColorSource,
      setTaskColorSource,
      setTaskDefaultCalendar,
      taskColorSourcePath,
      taskColorStaticOptions,
      taskDefaultCalendarSelection,
      taskPreviewSwatches,
      tasksDatabase,
      toggle,
      types,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "../../styles/settings.scss";

.wrapper .settings-page {
  .default-calendar {
    padding-bottom: 8px;

    .entry {
      border-bottom: none;

      label {
        flex-shrink: 0;
      }
    }
  }

  .color-source .entry {
    border-bottom: none;
    min-width: 0;

    label {
      flex-shrink: 0;
    }

    .color-control {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      column-gap: 10px;
      flex: 1;
      min-width: 0;
    }

    .color-preview {
      display: flex;
      align-items: center;
      justify-self: end;
      width: max-content;
      max-width: calc(14px * 5 + 4px * 4);
      min-width: 0;
      overflow: hidden;
      transition: max-width 300ms ease-out;

      :deep(.rtag) {
        flex-shrink: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 14px;
        transition:
          max-width 300ms ease-out,
          margin-inline-start 300ms ease-out;
      }

      :deep(.rtag:not(:first-child)) {
        margin-inline-start: 4px;
      }

      // Past 5, extras pack like TaskInline tags; the row clips them.
      &.overflow :deep(.rtag:nth-child(n + 6)) {
        margin-inline-start: 0;
      }

      &:hover {
        max-width: 100%;
      }

      &:hover :deep(.rtag) {
        max-width: 140px;
      }

      &:hover :deep(.rtag:not(:first-child)) {
        margin-inline-start: 2px;
        text-overflow: ellipsis;
      }

      @media (prefers-reduced-motion: reduce) {
        transition: none;

        :deep(.rtag) {
          transition: none;
        }
      }
    }
  }

  .calendars-section {
    display: flex;
    flex-direction: column;
    gap: 24px;

    .calendar-zone {
      .calendar-list {
        display: flex;
        flex-direction: column;
        gap: 1px;
        min-height: 60px;
        padding: 8px;
        border-radius: $border-radius-m;
        transition:
          background-color 200ms ease,
          border-color 200ms ease;
        border: 2px dashed transparent;

        &.drag-active {
          @include themed() {
            border-color: t($secondary-30);
          }
        }

        &.drop-over {
          @include themed() {
            border-color: t($secondary-50);
            background: t($secondary-10);
          }
        }

        :deep(.is-default) {
          .handle {
            pointer-events: none;
            opacity: 0.5;
            cursor: default;
          }
        }
      }
    }
  }

  .calendars {
    p {
      @extend %font-small-regular;
      @include themed() {
        color: t($grey-50);
      }
    }

    .calendar-owner {
      @extend %font-small-regular;
      margin-inline-end: 12px;

      @include themed() {
        color: t($grey-40);
      }
    }
  }
}

:deep(.calendar-select) {
  // Natural width on the right of its entry row, like .color-select: undo
  // the global `.v-select { flex: 1 }` stretch.
  flex: none;
  width: max-content;
  min-width: 210px;

  .vs__dropdown-toggle {
    min-width: 210px;
  }

  &.vs--open .vs__dropdown-toggle {
    border-radius: $border-radius-l;
    @include themed() {
      border-bottom-color: t($grey-15);
    }
  }

  .vs__dropdown-menu {
    min-width: 100%;
    width: max-content;
  }
}

.color-select {
  flex: none;
  width: max-content;
}

.select-calendar-color :deep(.rtag) {
  padding: 0;
  .box {
    height: 12px;
    width: 12px;
  }
}
</style>
